/**
 * AcademyTeaser.jsx
 * Free preview of the MbuziPlus Course by Imfuyo Academy: "Dairy Goat Basics".
 * Open to anyone, no sign-in. Four short lessons, a six-question check, then a
 * clear step to enrol for the full course. Progress is shown as a percentage
 * and kept on the reader's own device.
 *
 * Every fact is taken from a published source, listed under each lesson.
 */
import { useState, useEffect, useRef } from "react";
import { ArrowRight, ArrowLeft, Check, X, BookOpen, KeyRound } from "lucide-react";
import { db } from "../../Libs/firebase-config.mjs";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

/* ─── Preview tracking ───────────────────────────────────────
   Every visitor who starts the free preview gets a random id kept in
   this browser. Each step is recorded in Firestore under
   academy_previews/{id}, so Imfuyo can see how many people try the
   preview, how far they get, and who asked for an access code.   */
const PREVIEW_ID_KEY = "imfuyo_preview_id";
function previewId() {
  try {
    let id = localStorage.getItem(PREVIEW_ID_KEY);
    if (!id) {
      const buf = new Uint8Array(12);
      window.crypto.getRandomValues(buf);
      id = Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
      localStorage.setItem(PREVIEW_ID_KEY, id);
    }
    return id;
  } catch (e) { return null; }
}
function recordPreview(patch, isFirst) {
  const id = previewId();
  if (!id) return;
  const base = { lastSeenAt: serverTimestamp(), ...patch };
  if (isFirst) {
    base.startedAt = serverTimestamp();
    try { base.referrer = document.referrer || ""; base.path = window.location.pathname; base.screen = `${window.screen.width}x${window.screen.height}`; } catch (e) { /* ignore */ }
  }
  setDoc(doc(db, "academy_previews", id), base, { merge: true }).catch(() => { /* offline or blocked: ignore */ });
}

const GREEN = "#3E7A32";
const DEEP = "#285020";
const SAVE_KEY = "imfuyo-academy-teaser-v1";

const SRC = {
  wsu: { label: "Washington State University Extension, Dairy Goat Production", url: "https://wpcdn.web.wsu.edu/wp-extension/uploads/sites/2056/2023/05/Dairy-Goat-Production.pdf" },
  kalro: { label: "Kenya Agricultural and Livestock Research Organization, Dairy Goat Breeds and Breeding", url: "https://navcdp.kalro.org/docs/kcsap/DAIRY%20GOAT%20BREEDS%20AND%20BREEDING.pdf" },
  psu: { label: "Penn State Extension, Reproduction and Genetics", url: "https://extension.psu.edu/animals-and-livestock/goats/reproduction-and-genetics" },
};

const LESSONS = [
  {
    title: "Why dairy goats?",
    minutes: 3,
    intro: "Before you buy a single goat, understand what you are selling. Goat milk is close to cow milk, and a doe can work for you more than once a year.",
    items: [
      { h: "Milk that sits close to cow milk", p: "Average goat milk holds 3.2% protein, 3.8% fat and 4.1% lactose. Cow milk holds 3.5% protein, 3.6% fat and 4.7% lactose. Goat milk has a little less lactose and a little more calcium (0.19% against 0.18%)." },
      { h: "A doe can kid more than once a year", p: "A well-managed doe can have up to 1.5 kiddings a year. More kids and more lactations are how a small herd grows into a business, which is why records and timing matter so much." },
      { h: "Small animal, real enterprise", p: "Goats ask for less land than cattle. The price of that advantage is attention: feeding, breeding dates and health checks decide the result." },
    ],
    sources: [SRC.wsu, SRC.kalro],
  },
  {
    title: "The breeds you will meet in Kenya",
    minutes: 4,
    intro: "Three dairy breeds are common on Kenyan farms: the Saanen, the Alpine and the Toggenburg. They are not as different as the sales talk suggests.",
    items: [
      { h: "Read the range, not the headline", p: "The Kenya Agricultural and Livestock Research Organization gives average daily yields of 2 to 5 litres for the Saanen, 2 to 4 litres for the German and French Alpine, and 2 to 4 litres for the Toggenburg. The ranges overlap heavily." },
      { h: "What this means for a buyer", p: "A seller who promises 5 litres a day is quoting the top of one breed's range. Most does sit lower. Ask to see the doe milked, and ask what she is fed." },
      { h: "Where your milk really comes from", p: "Breed sets the range. Feed, health and breeding decide where in the range your doe lands. The next lesson shows how a goat turns feed into milk." },
    ],
    sources: [SRC.kalro],
  },
  {
    title: "How a goat turns feed into milk",
    minutes: 4,
    intro: "A goat is a ruminant. Its stomach is built to digest fibre, and a diet that forgets this costs you milk.",
    items: [
      { h: "Forage first", p: "Hay, silage and pasture should make up most of the daily diet. A high-grain diet with little fibre can cause indigestion and acidosis, and can lower the fat in the milk." },
      { h: "Feed grain to milk, not by habit", p: "For high-producing does, a guide is about 1 part grain for every 2.5 to 3 parts of milk produced. Grain mixes of 14% to 20% protein are suggested. Forages often lack enough minerals, so a mineral salt is usually needed." },
      { h: "Score the body", p: "Body condition is scored from 1 (very thin) to 5 (very fat). A doe should be at score 3 as she enters lactation. Checking by hand along the ribs and spine is a skill you will practise in the full course." },
    ],
    sources: [SRC.wsu],
  },
  {
    title: "Heat, breeding and kidding",
    minutes: 5,
    intro: "Milk starts with a kid. Timing the breeding is the one job that cannot be done late.",
    items: [
      { h: "The heat cycle", p: "A doe's cycle lasts 18 to 22 days. Heat lasts 12 to 48 hours, about 36 hours on average. Signs include restlessness, a red and swollen vulva, mucus discharge, tail wagging, bleating and frequent urination. A neighbouring buck makes the signs easier to see." },
      { h: "When to breed", p: "The Kenya Agricultural and Livestock Research Organization advises breeding 24 hours after heat begins, or every 12 hours until the heat ends. Unlike cattle, a goat standing to be mounted is not a reliable heat sign." },
      { h: "Counting to kidding", p: "Gestation is 148 to 152 days. Does can reach sexual maturity at 4 to 5 months, but are usually bred at 7 to 10 months, when they weigh about 32 to 36 kilograms." },
    ],
    sources: [SRC.kalro, SRC.psu, SRC.wsu],
  },
];

const QUIZ = [
  {
    q: "A doe shows clear heat signs on Monday at 6 in the morning. She has not been bred. When is the best time to breed her?",
    options: ["Immediately, before the heat ends", "About 24 hours after the heat began, so Tuesday morning", "Four days later, to be safe", "Only when she stands to be mounted by another doe"],
    answer: 1,
    why: "The Kenya Agricultural and Livestock Research Organization advises breeding about 24 hours after heat begins. Standing to be mounted is not a reliable heat sign in goats.",
  },
  {
    q: "A farmer feeds a lot of grain and little hay. The doe goes off her feed and the milk fat drops. What is the most likely cause?",
    options: ["Rumen upset from too much grain and too little fibre", "Too much clean water", "Her kids are too old", "The breed is wrong"],
    answer: 0,
    why: "A high-grain diet with low fibre can cause indigestion and acidosis, and can lower milk fat. Forage should make up most of the diet.",
  },
  {
    q: "A doe is bred on 1 March. Using a gestation of 148 to 152 days, when should you expect kidding?",
    options: ["Between 27 and 31 July", "Between 1 and 5 June", "In early September", "In late December"],
    answer: 0,
    why: "Count 148 to 152 days from 1 March. That gives 27 to 31 July in a normal year. Mark the window on your calendar and prepare the kidding pen early.",
  },
  {
    q: "A doe is giving 3 litres of milk a day. Using about 1 part grain to 2.5 to 3 parts milk, roughly how much grain does she need?",
    options: ["About 0.1 kilogram", "About 1 to 1.2 kilograms", "About 5 kilograms", "None, because grain is never needed"],
    answer: 1,
    why: "Three litres divided by 3 to 2.5 is 1 to 1.2. The guide is a starting point. Forage stays the base of the diet.",
  },
  {
    q: "A doe is at body condition score 2 and kids in four weeks. What does the guidance say you should aim for?",
    options: ["Leave her thin to keep the kid small", "Bring her to score 3 as she enters lactation, through better forage and minerals", "Aim for score 5", "Condition does not matter in goats"],
    answer: 1,
    why: "A doe should be at score 3 entering lactation. Score 5 is too fat. Improving forage and minerals early gives her reserves for milk.",
  },
  {
    q: "A buyer says: \"Goat milk has more lactose and less protein than cow milk.\" Using the averages in this lesson, what is correct?",
    options: ["The buyer is right on both points", "Goat milk has less lactose (4.1% against 4.7%) and slightly less protein (3.2% against 3.5%)", "Goat milk has more protein and less fat", "Both milks are identical in every value"],
    answer: 1,
    why: "Average goat milk has 4.1% lactose and 3.2% protein. Cow milk has 4.7% lactose and 3.5% protein.",
  },
];

const TOTAL = LESSONS.length + QUIZ.length; // 10 steps; the result screen is 100%

function useSaved() {
  const [saved, setSaved] = useState({ step: 0, answers: {} });
  const [resumed, setResumed] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (raw) {
        const v = JSON.parse(raw);
        if (typeof v.step === "number" && v.answers) { setSaved(v); if (v.step > 0 && v.step < LESSONS.length + QUIZ.length) setResumed(true); }
      }
    } catch (e) { /* storage may be unavailable */ }
  }, []);
  const save = (v) => {
    setSaved(v);
    try { localStorage.setItem(SAVE_KEY, JSON.stringify(v)); } catch (e) { /* ignore */ }
    const score = QUIZ.reduce((n, q, i) => n + (v.answers[i] === q.answer ? 1 : 0), 0);
    recordPreview({ step: v.step, answered: Object.keys(v.answers).length, score, completed: v.step >= TOTAL }, v.step === 0 && Object.keys(v.answers).length === 0);
  };
  return [saved, save, resumed];
}

export default function AcademyTeaser({ isDark, onEnrol, onEnterCode }) {
  const [saved, save, resumed] = useSaved();
  const [engaged, setEngaged] = useState(false);
  const [nudge, setNudge] = useState(null);
  const [card, setCard] = useState(0);
  const [hookDone, setHookDone] = useState(false);
  const [hookPick, setHookPick] = useState(null);
  const [celebrate, setCelebrate] = useState("");
  const [moved, setMoved] = useState(false);
  const engagedRef = useRef(false);
  const stepRef = useRef(0);
  const nudgedRef = useRef(false);
  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadSent, setLeadSent] = useState(false);
  const sendLead = () => {
    const name = leadName.trim(); const phone = leadPhone.trim();
    if (name.length < 2 || phone.replace(/\D/g, "").length < 9) return;
    recordPreview({ lead: { name, phone, at: new Date().toISOString() }, askedForKey: true }, false);
    setLeadSent(true);
    const msg = encodeURIComponent(`Hello Imfuyo, I finished the free MbuziPlus preview and I would like to join the full course. My name is ${name}, phone ${phone}.`);
    window.open("https://wa.me/254110563185?text=" + msg, "_blank", "noopener");
  };
  const { step, answers } = saved;

  const bg = isDark ? "#162418" : "#ffffff";
  const ink = isDark ? "#f3f6ef" : "#0d1f16";
  const sub = isDark ? "#b5c2b0" : "#4b5563";
  const line = isDark ? "#2b3d2d" : "#e3e8dd";
  const soft = isDark ? "#1d2e1f" : "#f1f5ee";

  const done = Math.min(step, TOTAL);
  const pct = step >= TOTAL ? 100 : Math.round((done / TOTAL) * 100);
  const score = QUIZ.reduce((n, q, i) => n + (answers[i] === q.answer ? 1 : 0), 0);

  stepRef.current = step;
  const toSection = () => { const el = document.getElementById("free-preview"); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 96, behavior: "smooth" }); };
  const go = (s) => { setMoved(true); setCard(0); save({ step: s, answers }); toSection(); };
  const answer = (i, choice) => { if (answers[i] === undefined) save({ step: step, answers: { ...answers, [i]: choice } }); };
  const restart = () => { setCard(0); setHookDone(true); save({ step: 0, answers: {} }); };

  // The first touch inside the preview counts as "started": it is recorded once.
  const engage = () => {
    if (engagedRef.current) return;
    engagedRef.current = true;
    setEngaged(true);
    try { document.body.classList.add("tf-engaged"); } catch (e) { /* ignore */ }
    recordPreview({ step, answered: Object.keys(answers).length, score, completed: step >= TOTAL }, step === 0 && Object.keys(answers).length === 0);
  };

  // "Try it free" elsewhere on the page brings the visitor here in one tap.
  useEffect(() => {
    const onStart = () => { engage(); toSection(); };
    window.addEventListener("imfuyo-start-preview", onStart);
    return () => window.removeEventListener("imfuyo-start-preview", onStart);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // The Services page can bring a visitor straight to the free lesson.
  useEffect(() => {
    let want = null;
    try { want = sessionStorage.getItem("imfuyo-scroll-to"); if (want === "free-preview") sessionStorage.removeItem("imfuyo-scroll-to"); } catch (e) { /* ignore */ }
    if (want !== "free-preview") return undefined;
    const t = setTimeout(() => toSection(), 900);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Gentle, once-per-visit nudges: never a blocking window, never after the visitor has finished.
  useEffect(() => {
    const inView = () => {
      const el = document.getElementById("free-preview");
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return r.top < window.innerHeight * 0.7 && r.bottom > window.innerHeight * 0.3;
    };
    const show = (kind) => {
      if (nudgedRef.current || stepRef.current >= TOTAL) return;
      try { if (sessionStorage.getItem("imfuyo-preview-nudged")) return; sessionStorage.setItem("imfuyo-preview-nudged", "1"); } catch (e) { /* ignore */ }
      nudgedRef.current = true;
      setNudge(kind);
      if (engagedRef.current) recordPreview({ nudge: kind }, false);
    };
    const onLeave = (e) => { if (e.clientY <= 0 && engagedRef.current) show("exit"); };
    let away = false;
    const onVis = () => {
      if (document.visibilityState === "hidden") { away = engagedRef.current; return; }
      if (away && !inView()) show("back");
      away = false;
    };
    const timer = setTimeout(() => { if (!engagedRef.current && !inView()) show("try"); }, 25000);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("visibilitychange", onVis);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", onLeave); document.removeEventListener("visibilitychange", onVis); try { document.body.classList.remove("tf-engaged"); } catch (e) { /* ignore */ } };
  }, []);

  useEffect(() => {
    if (!celebrate) return undefined;
    const t = setTimeout(() => setCelebrate(""), 3500);
    return () => clearTimeout(t);
  }, [celebrate]);

  // While the visitor is on another tab, the tab title gently calls them back.
  useEffect(() => {
    const original = document.title;
    let timer = null;
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } document.title = original; };
    const onVis = () => {
      if (document.visibilityState === "hidden") {
        if (!engagedRef.current || stepRef.current >= TOTAL || timer) return;
        let on = false;
        timer = setInterval(() => { on = !on; document.title = on ? "Your goat lesson is waiting" : original; }, 1800);
      } else { stop(); }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); stop(); };
  }, []);

  const placeLink = () => {
    const msg = encodeURIComponent(`Hello Imfuyo, please save my place in the free MbuziPlus preview. I am at step ${Math.min(stepRef.current + 1, TOTAL)} of ${TOTAL}. Please send me the link to continue.`);
    return "https://wa.me/254110563185?text=" + msg;
  };
  const savePlace = () => { if (engagedRef.current) recordPreview({ savedPlace: true }, false); };
  const stepWord = step < LESSONS.length ? `This lesson takes about ${LESSONS[step].minutes} minutes.` : `Only ${TOTAL - step} question${TOTAL - step === 1 ? "" : "s"} left.`;
  const cheer = step === 0 ? "Lesson 1 takes about 3 minutes, one short idea at a time."
    : step === 1 ? "Great start. Three lessons to go."
    : step === 2 ? "You are halfway through the lessons."
    : step === 3 ? "Last lesson, then a short challenge."
    : step < TOTAL - 1 ? `Challenge time. ${TOTAL - step} questions to go.`
    : step === TOTAL - 1 ? "Last question!" : "";

  const navBar = { position: "sticky", bottom: 0, zIndex: 3, display: "flex", gap: 10, flexWrap: "wrap", marginTop: 24, padding: "12px 0 calc(12px + env(safe-area-inset-bottom, 0px))", background: bg, borderTop: `1px solid ${line}` };

  const btn = (primary) => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    padding: "13px 22px", borderRadius: 999, fontWeight: 700, fontSize: 14.5, fontFamily: "inherit", cursor: "pointer",
    border: primary ? "none" : `1.5px solid ${line}`, background: primary ? DEEP : "transparent", color: primary ? "#fff" : ink,
  });

  const Progress = () => (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, fontWeight: 700, color: sub, marginBottom: 6 }}>
        <span>{step >= TOTAL ? "Free preview complete" : `Step ${Math.min(step + 1, TOTAL)} of ${TOTAL}`}</span>
        <span aria-live="polite">{pct}% complete</span>
      </div>
      <div role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} style={{ height: 8, borderRadius: 99, background: soft, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: `linear-gradient(90deg, ${GREEN}, ${DEEP})`, transition: "width .4s ease" }} />
      </div>
      {cheer && <p style={{ margin: "8px 0 0", fontSize: 14, fontWeight: 600, color: GREEN }}>{cheer}</p>}
    </div>
  );

  const EnrolStrip = () => (
    <p style={{ margin: "26px 0 0", padding: "14px 16px", borderRadius: 14, background: soft, color: sub, fontSize: 13.5, lineHeight: 1.6 }}>
      This is the free preview. <strong style={{ color: ink }}>Enrol for the full MbuziPlus Course by Imfuyo Academy</strong> for the complete lessons, farm exercises, the final exam and your certificate.
    </p>
  );

  let body;
  if (step < LESSONS.length) {
    const l = LESSONS[step];
    const last = card >= l.items.length - 1;
    const it = l.items[card];
    const nextCard = () => {
      if (!last) { setMoved(true); setCard(card + 1); recordPreview({ step, card: card + 1 }, false); toSection(); return; }
      setCelebrate(step === LESSONS.length - 1 ? "All four lessons done. Now a short challenge!" : `Lesson ${step + 1} done. Well done!`);
      go(step + 1);
    };
    const backCard = () => { if (card > 0) { setCard(card - 1); toSection(); } else if (step > 0) go(step - 1); };
    if (step === 0 && card === 0 && !hookDone) {
      const hookOpts = ["Less than 1 litre", "2 to 5 litres", "More than 8 litres"];
      body = (
        <div>
          <p style={{ margin: "0 0 14px", color: sub, fontSize: 16, lineHeight: 1.6 }}>Welcome! Free, no sign-in, no payment. Take a 5 to 20 minute lesson: four short lessons and a six-question challenge. First, one quick question.</p>
          <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: GREEN, margin: "0 0 6px" }}>Quick question · 20 seconds</p>
          <h3 style={{ fontSize: "clamp(22px,3vw,28px)", margin: "0 0 16px", color: ink, lineHeight: 1.25 }}>How much milk does one dairy goat give in a day?</h3>
          <div style={{ display: "grid", gap: 10 }}>
            {hookOpts.map((o, i) => {
              const reveal = hookPick !== null, right = i === 1, isPicked = hookPick === i;
              return (
                <button key={o} disabled={reveal} onClick={() => setHookPick(i)}
                  style={{ textAlign: "left", padding: "14px 16px", borderRadius: 14, border: `2px solid ${reveal ? (right ? GREEN : isPicked ? "#B42318" : line) : line}`, background: reveal && right ? (isDark ? "#1d3a1f" : "#eef6ea") : bg, color: ink, fontSize: 16, fontFamily: "inherit", cursor: reveal ? "default" : "pointer", display: "flex", gap: 10, alignItems: "center", minHeight: 52 }}>
                  <span style={{ flex: 1 }}>{o}</span>
                  {reveal && right && <Check size={18} color={GREEN} />}
                  {reveal && isPicked && !right && <X size={18} color="#B42318" />}
                </button>
              );
            })}
          </div>
          {hookPick !== null && (
            <p className="tf-pop" style={{ margin: "16px 0 0", padding: "14px 16px", borderRadius: 14, background: soft, color: ink, fontSize: 15, lineHeight: 1.65 }}>
              <strong>{hookPick === 1 ? "Correct. " : "Good guess. "}</strong>The Kenya Agricultural and Livestock Research Organization gives 2 to 5 litres a day for the Saanen, and 2 to 4 litres for the Alpine and Toggenburg. Lesson 1 shows why this matters for your income.
            </p>
          )}
          <div style={navBar}>
            <button style={{ ...btn(true), background: "#BF4700", marginLeft: "auto", opacity: hookPick === null ? .5 : 1 }} disabled={hookPick === null} onClick={() => { setHookDone(true); toSection(); }}>Start lesson 1 <ArrowRight size={16} /></button>
          </div>
          <p style={{ margin: "4px 0 0" }}><button onClick={() => setHookDone(true)} style={{ background: "none", border: "none", color: sub, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", fontSize: 13.5, padding: 0 }}>Skip the question</button></p>
        </div>
      );
    } else {
      body = (
        <div>
          <Progress />
          {celebrate && <p className="tf-pop" role="status" style={{ margin: "0 0 14px", padding: "10px 14px", borderRadius: 12, background: isDark ? "#1d3a1f" : "#eef6ea", color: GREEN, fontWeight: 800, fontSize: 15 }}><Check size={16} style={{ verticalAlign: "-3px" }} /> {celebrate}</p>}
          <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: GREEN, margin: "0 0 6px" }}>Lesson {step + 1} of {LESSONS.length} · {l.minutes} min · Part {card + 1} of {l.items.length}</p>
          <h3 style={{ fontSize: "clamp(24px,3.4vw,32px)", margin: "0 0 12px", color: ink, letterSpacing: "-0.02em", lineHeight: 1.15 }}>{l.title}</h3>
          {card === 0 && <p style={{ fontSize: 17, lineHeight: 1.7, color: sub, margin: "0 0 20px", maxWidth: 640 }}>{l.intro}</p>}
          <div key={`${step}-${card}`} className="tf-card" style={{ borderLeft: `4px solid ${GREEN}`, padding: "2px 0 2px 16px" }}>
            <h4 style={{ margin: "0 0 6px", fontSize: 19, color: ink }}>{it.h}</h4>
            <p style={{ margin: 0, fontSize: 17, lineHeight: 1.7, color: sub, maxWidth: 640 }}>{it.p}</p>
          </div>
          <div aria-hidden="true" style={{ display: "flex", gap: 6, marginTop: 18 }}>
            {l.items.map((x, i) => <span key={x.h} style={{ width: i === card ? 22 : 8, height: 8, borderRadius: 99, background: i <= card ? GREEN : line, transition: "width .25s ease" }} />)}
          </div>
          {last && (
            <p style={{ margin: "16px 0 0", fontSize: 12.5, color: sub, lineHeight: 1.6 }}>
              Sources: {l.sources.map((s2, i) => (
                <span key={s2.url}>{i ? "; " : ""}<a href={s2.url} target="_blank" rel="noreferrer" style={{ color: GREEN }}>{s2.label}</a></span>
              ))}
            </p>
          )}
          <div style={navBar}>
            {(card > 0 || step > 0) && <button style={btn(false)} onClick={backCard}><ArrowLeft size={16} /> Back</button>}
            <button style={{ ...btn(true), background: "#BF4700", marginLeft: "auto" }} onClick={nextCard}>
              {!last ? "Next" : step === LESSONS.length - 1 ? "Take the challenge" : "Next lesson"} <ArrowRight size={16} />
            </button>
          </div>
          {(step > 0 || card > 0) && <p style={{ margin: "2px 0 0", fontSize: 13.5, color: sub }}>Short of time? <a href={placeLink()} target="_blank" rel="noreferrer" onClick={savePlace} style={{ color: GREEN, fontWeight: 700 }}>Save my place on WhatsApp</a></p>}
          <EnrolStrip />
        </div>
      );
    }
  } else if (step < TOTAL) {
    const qi = step - LESSONS.length;
    const q = QUIZ[qi];
    const picked = answers[qi];
    body = (
      <div>
        <Progress />
        <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: GREEN, margin: "0 0 6px" }}>Challenge · Question {qi + 1} of {QUIZ.length}</p>
        <h3 style={{ fontSize: "clamp(20px,2.8vw,26px)", margin: "0 0 18px", color: ink, lineHeight: 1.3 }}>{q.q}</h3>
        <div style={{ display: "grid", gap: 10 }}>
          {q.options.map((o, i) => {
            const isPicked = picked === i, right = i === q.answer, reveal = picked !== undefined;
            const col = reveal ? (right ? GREEN : isPicked ? "#B42318" : line) : line;
            return (
              <button key={o} onClick={() => answer(qi, i)} disabled={reveal}
                style={{ textAlign: "left", padding: "14px 16px", borderRadius: 14, border: `2px solid ${col}`, background: reveal && right ? (isDark ? "#1d3a1f" : "#eef6ea") : bg, color: ink, fontSize: 15.5, lineHeight: 1.5, cursor: reveal ? "default" : "pointer", fontFamily: "inherit", display: "flex", gap: 10, alignItems: "flex-start" }}>
                <span style={{ flex: 1 }}>{o}</span>
                {reveal && right && <Check size={18} color={GREEN} />}
                {reveal && isPicked && !right && <X size={18} color="#B42318" />}
              </button>
            );
          })}
        </div>
        {picked !== undefined && (
          <p style={{ margin: "16px 0 0", padding: "14px 16px", borderRadius: 14, background: soft, color: ink, fontSize: 15, lineHeight: 1.65 }}>
            <strong>{picked === q.answer ? "Correct. " : "Not quite. "}</strong>{q.why}
          </p>
        )}
        <div style={navBar}>
          <button style={btn(false)} onClick={() => go(step - 1)}><ArrowLeft size={16} /> Back</button>
          <button style={{ ...btn(true), opacity: picked === undefined ? .45 : 1 }} disabled={picked === undefined} onClick={() => go(step + 1)}>
            {qi === QUIZ.length - 1 ? "See my result" : "Next question"} <ArrowRight size={16} />
          </button>
        </div>
        <EnrolStrip />
      </div>
    );
  } else {
    body = (
      <div>
        <Progress />
        <p style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: GREEN, margin: "0 0 6px" }}>Free preview complete</p>
        <h3 style={{ fontSize: "clamp(26px,3.6vw,36px)", margin: "0 0 10px", color: ink, letterSpacing: "-0.02em" }}>Well done, MbuziPlus Student.</h3>
        <p style={{ fontSize: 17, lineHeight: 1.7, color: sub, margin: "0 0 18px", maxWidth: 620 }}>
          You scored <strong style={{ color: ink }}>{score} out of {QUIZ.length}</strong> on the challenge. {score >= 5 ? "Strong result: you are ready for the full course." : score >= 3 ? "A good start. The full course goes deeper on each of these topics." : "Every goat farmer starts here. The full course teaches each topic step by step."}
        </p>
        <div style={{ padding: 20, borderRadius: 18, background: soft, border: `1.5px solid ${line}` }}>
          <h4 style={{ margin: "0 0 8px", fontSize: 18, color: ink }}>Continue with the full MbuziPlus Course</h4>
          <ul style={{ margin: "0 0 16px", paddingLeft: 20, color: sub, lineHeight: 1.8, fontSize: 15.5 }}>
            <li>All 8 parts and 42 modules: from your first goat to milk, markets, insurance and loans</li>
            <li>Quizzes, journal and your progress saved to your own account</li>
            <li>A final examination and a Certificate of Completion with your name</li>
            <li>One access key is 4,900 Kenya Shillings. Groups of five or more can ask for a group key</li>
          </ul>
          {leadSent ? (
            <p style={{ margin: "0 0 12px", fontSize: 15, color: ink, fontWeight: 700 }}><Check size={16} color={GREEN} style={{ verticalAlign: "-3px" }} /> Thank you, {leadName.trim().split(" ")[0]}. WhatsApp has opened with your message; send it and the Imfuyo team will reply with your access code and next steps.</p>
          ) : (
            <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr", marginBottom: 14 }}>
              <p style={{ margin: 0, fontSize: 14.5, color: sub }}>Get your access code: leave your name and phone, and we will send the next steps on WhatsApp.</p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Your full name" style={{ flex: "1 1 180px", height: 46, borderRadius: 12, border: `1.5px solid ${line}`, padding: "0 14px", fontSize: 15, fontFamily: "inherit", background: bg, color: ink, outline: "none" }} />
                <input value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)} placeholder="Your phone, 07XX XXX XXX" inputMode="tel" style={{ flex: "1 1 180px", height: 46, borderRadius: 12, border: `1.5px solid ${line}`, padding: "0 14px", fontSize: 15, fontFamily: "inherit", background: bg, color: ink, outline: "none" }} />
                <button style={btn(true)} onClick={sendLead}>Send me the next steps <ArrowRight size={16} /></button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button style={btn(false)} onClick={onEnrol}>Register on WhatsApp <ArrowRight size={16} /></button>
            <button style={btn(false)} onClick={onEnterCode}><KeyRound size={15} /> I have a code</button>
          </div>
        </div>
        <button onClick={restart} style={{ marginTop: 16, background: "none", border: "none", color: GREEN, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: 14 }}>Take the preview again</button>
      </div>
    );
  }

  return (
    <section id="free-preview" onPointerDown={engage} onFocus={engage} style={{ maxWidth: 1100, margin: "0 auto", padding: "0 clamp(16px, 3vw, 32px) 72px" }}>
      <div style={{ background: bg, border: `1px solid ${line}`, borderRadius: 24, padding: "clamp(20px,4vw,40px)", boxShadow: isDark ? "none" : "0 12px 40px rgba(13,31,22,.07)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <BookOpen size={15} color={GREEN} />
          <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: ".1em", textTransform: "uppercase", color: GREEN }}>Free preview · No sign-in needed</span>
        </div>
        <h2 style={{ fontSize: "clamp(28px,4vw,42px)", margin: "0 0 18px", color: ink, letterSpacing: "-0.03em", lineHeight: 1.08 }}>
          Dairy Goat Basics <span style={{ color: GREEN }}>from the MbuziPlus Course</span>
        </h2>
        {step === 0 && card === 0 && hookDone && !moved && (
          <p style={{ margin: "0 0 18px", color: sub, fontSize: 16, lineHeight: 1.6 }}>Free, no sign-in, no payment. Take a 5 to 20 minute lesson: four short lessons and a six-question challenge.</p>
        )}
        {resumed && !moved && (
          <p style={{ margin: "0 0 18px", padding: "12px 14px", borderRadius: 12, background: soft, color: ink, fontSize: 15, fontWeight: 600 }}>Welcome back. You can continue at step {Math.min(step + 1, TOTAL)} of {TOTAL}.</p>
        )}
        {body}
      </div>
      {nudge && (
        <div role="status" className="tf-nudge" style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", top: "calc(12px + env(safe-area-inset-top, 0px))", zIndex: 2000, width: "min(440px, calc(100vw - 32px))", display: "flex", gap: 12, alignItems: "center", padding: "12px 14px", borderRadius: 16, background: DEEP, color: "#fff", boxShadow: "0 12px 36px rgba(0,0,0,.28)" }}>
          <span style={{ flex: 1, fontSize: 14.5, lineHeight: 1.45, fontWeight: 600 }}>
            {nudge === "try" ? "Try one free lesson. It takes about 3 minutes." : nudge === "back" ? `Welcome back. You are on step ${Math.min(step + 1, TOTAL)} of ${TOTAL}. ${stepWord}` : `Before you go: you are on step ${Math.min(step + 1, TOTAL)} of ${TOTAL}. ${stepWord} Shall we continue?`}
            {nudge !== "try" && <a href={placeLink()} target="_blank" rel="noreferrer" onClick={savePlace} style={{ display: "block", marginTop: 4, color: "#F4D9B8", fontSize: 13, fontWeight: 600 }}>No time now? Save my place on WhatsApp</a>}
          </span>
          <button className="tf-go" onClick={() => { setNudge(null); engage(); toSection(); }} style={{ background: "#BF4700", color: "#fff", border: "none", borderRadius: 999, padding: "10px 16px", fontWeight: 800, fontSize: 14, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap" }}>{nudge === "try" ? "Start" : "Continue"}</button>
          <button aria-label="Close" onClick={() => setNudge(null)} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", padding: 4, display: "inline-flex" }}><X size={18} /></button>
        </div>
      )}
      <style>{`body.tf-engaged .al-bar { display: none !important; }
@keyframes tfIn { from { opacity: 0; transform: translate(-50%, -16px); } to { opacity: 1; transform: translate(-50%, 0); } }
@keyframes tfPulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(191,71,0,.55); } 60% { box-shadow: 0 0 0 10px rgba(191,71,0,0); } }
@keyframes tfPop { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
.tf-nudge { animation: tfIn .45s ease-out both; }
.tf-nudge .tf-go { animation: tfPulse 1.8s ease-out 3; }
.tf-pop, .tf-card { animation: tfPop .3s ease-out both; }
@media (prefers-reduced-motion: reduce) { .tf-nudge, .tf-nudge .tf-go, .tf-pop, .tf-card { animation: none; } }`}</style>
    </section>
  );
}
